import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  IconButton,
  TableCell,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Edit, Trash2 } from "lucide-react";
import DataTable from "bundles/Internal/components/common/DataTable";
import CreditTableFilters from "bundles/Internal/components/credit/CreditTableFilters";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

export const COLUMNS_CONSTANTS = [
  { key: "id", label: "Id" },
  { key: "company_name", label: "Company" },
  { key: "credit_type", label: "Type" },
  { key: "status", label: "Status" },
  { key: "expire_at", label: "Expires at" },
  { key: "recruiter_id", label: "Recruiter" },
  { key: "job_offer_id", label: "Job offer" },
  { key: "created_at", label: "Created at" },
  { key: "updated_at", label: "Updated at" },
  { key: "created_by", label: "Created by" },
];

const STATUS_COLOR = {
  pending: { bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
  available: { bg: "rgba(7,147,68,0.10)", text: "#079344" },
  activated: { bg: "rgba(33,150,243,0.10)", text: "#2196F3" },
  expired: { bg: "rgba(211,47,47,0.10)", text: "#d32f2f" },
};

const CREDIT_TYPE_LABEL = {
  job_offer_creation: "Job offer",
  cv_access: "CV access",
  licence: "Licence",
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

function formatCreditCell(credit, key) {
  const value = credit?.[key];

  if (key === "status" && value) {
    const c = STATUS_COLOR[value] || STATUS_COLOR.pending;
    return (
      <Chip
        label={value}
        size="small"
        sx={{
          backgroundColor: c.bg,
          color: c.text,
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          fontSize: 11,
          fontWeight: 600,
          borderRadius: "8px",
          height: 24,
        }}
      />
    );
  }

  if (key === "credit_type") return CREDIT_TYPE_LABEL[value] || value || "";
  if (key === "expire_at") return formatDate(value);
  if (key === "created_at") return formatDateTime(value);
  if (key === "updated_at") return formatDateTime(value);

  if (key === "created_by") {
    const c = credit?.created_by;
    if (!c) return "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <UserAvatar user={c} size={22} />
        <Box component="span">{c.full_name || c.email || "Unknown"}</Box>
      </Box>
    );
  }

  if (value === null || value === undefined) return "—";
  return String(value);
}

const CreditTable = ({
  credits = [],
  onQueryChange,
  filters,
  onFiltersChange,
  onEdit,
  onDelete,
  onAddClick,
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
}) => {
  const navigate = useNavigate();
  const columns = useMemo(() => COLUMNS_CONSTANTS, []);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCredit, setMenuCredit] = useState(null);
  const renderCell = useCallback(
    (credit, key) => {
      if (key === "company_name") {
        const value = credit?.company_name;
        if (!value) return "—";
        return (
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              if (credit.company_id) navigate(`/internal/companies/${credit.company_id}`);
            }}
            sx={{
              color: "#079344",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {value}
          </Box>
        );
      }
      if (key === "job_offer_id") {
        if (!credit?.job_offer_id) return "—";
        return (
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/internal/job_offers/${credit.job_offer_id}`);
            }}
            sx={{
              color: "#079344",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {credit.job_offer_title || `#${credit.job_offer_id}`}
          </Box>
        );
      }

      if (key === "recruiter_id") {
        if (!credit?.recruiter_id) return "—";
        return (
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/internal/recruiters/${credit.recruiter_id}`);
            }}
            sx={{
              color: "#079344",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {credit.recruiter_name || `#${credit.recruiter_id}`}
          </Box>
        );
      }

      return formatCreditCell(credit, key);
    },
    [navigate],
  );

  return (
    <>
      <DataTable
        title="Credits"
        totalLabel={(count) => `${count.toLocaleString()} total credits`}
        emptyTitle="No credits found"
        emptySubtitle="Try adjusting your search criteria"
        columns={columns}
        data={credits}
        renderCell={renderCell}
        onAddClick={onAddClick}
        addButtonLabel="Add Credits"
        onQueryChange={onQueryChange}
        searchPlaceholder="Search by company name..."
        searchInfo="Company name"
        searchById
        filtersComponent={
          filters && onFiltersChange ? (
            <CreditTableFilters filters={filters} onFiltersChange={onFiltersChange} />
          ) : null
        }
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        loading={loading}
        tailHeadColumns={[""]}
        renderTailCells={(credit) => (
          <TableCell key="actions" sx={{ whiteSpace: "nowrap", py: 1.5, width: 48 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchor(e.currentTarget);
                setMenuCredit(credit);
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </TableCell>
        )}
      />
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuCredit(null); }}
        onClick={() => { setMenuAnchor(null); }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => { onEdit?.(menuCredit); setMenuCredit(null); }}>
          <ListItemIcon><Edit size={16} /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { onDelete?.(menuCredit); setMenuCredit(null); }}>
          <ListItemIcon><Trash2 size={16} color="#d32f2f" /></ListItemIcon>
          <ListItemText sx={{ color: "#d32f2f" }}>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default CreditTable;
